import { responseModel, responseDate, responseAnswers, responseOptions } from "./survey-response-model";

const answerText = (answer) => [answer.answer, answer.custom_answer].filter((value) => value != null && String(value) !== "").map(String).join(" - ");
const field = (label, value) => ({ stack: [{ text: label.toUpperCase(), style: "label" }, { text: value || "—", margin: [0, 4, 0, 0] }] });
const safeName = (name) => Array.from(String(name), (char) => char.codePointAt(0) < 32 ? "-" : char).join("").replace(/[<>:"/\\|?*]/g, "-").replace(/\s+/g, " ").trim().slice(0, 80) || "Survey response";

export const surveyResponseFilename = (survey, result) => {
  const { definition, guestName } = responseModel(survey, result);
  return `${safeName(definition.name || survey?.name || "Survey response")}-${safeName(guestName)}-${safeName(result.survey_results_id || "response")}.pdf`;
};

export const surveyResponseDocument = (survey, result) => {
  const { definition, context, guestName, questions, submitted } = responseModel(survey, result);
  const title = definition.name || survey?.name || "Survey response";
  const content = [
    { text: title, style: "title" },
    { columns: [field("Guest", guestName), field("Property", [context.property || context.pms_id, context.rental_address].filter(Boolean).join("\n")), field("Check-In - Check-Out", `${responseDate(context.check_in)} - ${responseDate(context.check_out)}`)], columnGap: 20, margin: [0, 0, 0, 18] },
    { ...field("Submitted", submitted), margin: [0, 0, 0, 16] },
  ];
  if (definition.intro) content.push({ text: definition.intro, margin: [0, 0, 0, 10] });
  let count = 0;
  questions.forEach((question) => {
    const answers = responseAnswers(result, question.survey_question_id);
    if (!answers.length && !result.definition_snapshot) return;
    count += 1;
    content.push({ text: question.question || "Question", style: "question", headlineLevel: 1 });
    if (question.help_text) content.push({ text: question.help_text, style: "help", margin: [0, 0, 0, 6] });
    if (question.question_type === "item-list") {
      const value = answers[0]?.value;
      if (!value?.items?.length) content.push({ text: "No items selected" });
      else value.items.forEach((item) => {
        content.push({ text: `${item.name}${item.quantity != null ? ` × ${item.quantity}` : ""}${item.unit ? ` ${item.unit}` : ""}`, margin: [0, 0, 0, 3] });
        if (item.preference) content.push({ text: item.preference, margin: [10, 0, 0, 6] });
      });
      if (value?.notes) content.push({ text: `Notes: ${value.notes}`, margin: [0, 6, 0, 0] });
    } else if (["multiple-choice", "checkboxes"].includes(question.question_type)) {
      const options = responseOptions(question);
      if (question.include_other && !options.some((o) => o.answer === "Other")) options.push({ answer: "Other" });
      options.forEach((option) => content.push({ text: `${answers.some((a) => String(a.answer) === String(option.answer)) ? "[x]" : "[ ]"} ${option.answer}`, margin: [0, 0, 0, 4] }));
      answers.filter((a) => a.custom_answer || !options.some((o) => String(o.answer) === String(a.answer))).forEach((answer) => {
        const text = answerText(answer);
        if (text) content.push({ text, margin: [0, 4, 0, 0] });
      });
      if (!answers.some((a) => answerText(a))) content.push({ text: "Not answered", style: "help" });
    } else {
      const text = answers.map(answerText).filter(Boolean).join("\n") || "Not answered";
      content.push({ text: /^range-(5|10)$/.test(question.question_type) ? `${text} / ${question.question_type.split("-")[1]}` : text });
    }
  });
  if (!count) content.push({ text: "No answers available.", style: "help" });
  return {
    pageSize: "LETTER", pageMargins: [42, 40, 42, 48],
    info: { title, subject: `Survey response - ${guestName}`, creator: "RueBaRue" },
    defaultStyle: { font: "Roboto", fontSize: 10, color: "#272841", lineHeight: 1.25 },
    styles: { title: { fontSize: 19, bold: true, margin: [0, 0, 0, 24] }, label: { fontSize: 8, color: "#646575" }, question: { fontSize: 12, bold: true, margin: [0, 16, 0, 8] }, help: { fontSize: 9, color: "#646575" } },
    content,
    footer: (page, pages) => ({ text: `${page} / ${pages}`, alignment: "right", margin: [42, 16, 42, 0], fontSize: 8, color: "#646575" }),
    pageBreakBefore: (node, container) => node.headlineLevel === 1 && container.getFollowingNodesOnPage().length === 0,
  };
};

export const downloadSurveyResponsePdf = async (survey, result) => {
  const [engine, fonts] = await Promise.all([import("pdfmake/build/pdfmake"), import("pdfmake/build/vfs_fonts")]);
  const pdfMake = engine.default || engine;
  pdfMake.addVirtualFileSystem(fonts.default || fonts);
  await pdfMake.createPdf(surveyResponseDocument(survey, result)).download(surveyResponseFilename(survey, result));
};
